/* eslint-disable react-hooks/exhaustive-deps */
import easingFunctions from '../../../app/helpers/easing';
import camelToWords from '../../../helpers/camelToWords';
import { globalUpdatePresetOrProperty } from '../../../store/global/actions';
import Back from '../Back';
import Content from '../Content';
import editStyles from '../EditProperty/EditProperty.module.scss';
import rotationStyles from '../Rotation/Rotation.module.scss';
import useGetValue from '../hooks/useGetValue';
import BezierSplineEditor from './BezierSplineEditor';
import styles from './CubicBezier.module.scss';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import ReactJson from 'react-json-view';
import { useDispatch } from 'react-redux';

const axis = ['p0x', 'p0y', 'p1x', 'p1y'];

const getPoints = (value) => {
  if (value[0] === 'bezier') {
    return value.slice(1);
  }

  return [0.25, 0.25, 0.75, 0.75];
};

const getEasingFunction = (value) => {
  if (value[0] === 'bezier') {
    return 'linear';
  }

  return value[1];
};

const CubicBezierInner = ({ payload, revert }) => {
  const currentValue = useGetValue(payload.id);
  const defaultValue = useGetValue(`default.${payload.id}`);
  const name = useMemo(() => camelToWords(payload.name), [payload.name]);
  const dispatch = useDispatch();
  const easingFunctionsKeys = useMemo(() => Object.keys(easingFunctions), []);

  const isBezier = currentValue[0] === 'bezier';

  const about = useMemo(() => {
    return {
      key: payload.id,
      name: payload.name,
      type: 'Number[]',
      defaultValue,
    };
  }, [payload, defaultValue]);

  const [points, setPoints] = useState(getPoints(currentValue));
  const [easing, setEasing] = useState(getEasingFunction(currentValue));

  const p0 = useMemo(() => points.slice(0, 2), [points]);
  const p1 = useMemo(() => points.slice(2), [points]);

  const confirm = () => {
    if (isBezier) {
      dispatch(globalUpdatePresetOrProperty(payload.id, ['bezier', ...points]));

      if (payload.commitAction) {
        dispatch(payload.commitAction);
      }
    }
  };

  return (
    <>
      <Back title={name} index={payload.menuIndex} />

      <Content>
        <div className={styles.bezierDiagram}>
          <BezierSplineEditor points={points} onPointsChange={setPoints} isBezierMain={isBezier} easingFn={easing} confirm={confirm} />
        </div>

        <div className={styles.bezier}>
          <div className={editStyles.checkboxWrapper}>
            <div className={editStyles.checkbox}>
              <input
                type="checkbox"
                className={editStyles.ikxBAC}
                onChange={(e) => {
                  const nowIsBezier = e.target.checked;
                  const value = [];

                  if (nowIsBezier) {
                    value.push('bezier', ...points);
                  } else {
                    value.push('easing', easing);
                  }

                  dispatch(globalUpdatePresetOrProperty(payload.id, value));

                  if (payload.commitAction) {
                    dispatch(payload.commitAction);
                  }
                }}
                checked={Boolean(isBezier)}
              />
            </div>
            <span>{isBezier ? 'Bezier curve' : 'Easing function'}</span>
          </div>
        </div>

        <div className={cn(styles.box, isBezier && styles.selected)}>
          <div className={rotationStyles.values}>
            {p0.map((light, index) => (
              <div>
                <span>{axis[index]}:</span>
                {light.toFixed(5)}
              </div>
            ))}
          </div>

          <div className={rotationStyles.values}>
            {p1.map((light, index) => (
              <div>
                <span>{axis[2 + index]}:</span>
                {light.toFixed(5)}
              </div>
            ))}
          </div>
        </div>

        <div className={cn(styles.box, !isBezier && styles.selected)}>
          <div className={styles.select}>
            <select
              value={easing}
              onChange={(e) => {
                setEasing(e.target.value);

                if (!isBezier) {
                  dispatch(globalUpdatePresetOrProperty(payload.id, ['easing', e.target.value]));

                  if (payload.commitAction) {
                    dispatch(payload.commitAction);
                  }
                }
              }}
            >
              {easingFunctionsKeys.map((key) => (
                <option value={key}>{key}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={rotationStyles.default}>
          <button
            onClick={() => {
              revert();
            }}
          >
            Revert default
          </button>
        </div>

        <div className={editStyles.details}>
          <ReactJson src={about} theme="tomorrow" name="field" />
        </div>
      </Content>
    </>
  );
};

const CubicBezier = ({ payload }) => {
  const defaultValue = useGetValue(`default.${payload.id}`);
  const dispatch = useDispatch();
  const [key, setKey] = useState(Date.now());

  const revert = () => {
    dispatch(globalUpdatePresetOrProperty(payload.id, defaultValue));

    setKey(Date.now());
  };

  return <CubicBezierInner payload={payload} revert={revert} key={key} />;
};

export default CubicBezier;
